import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useTranslation } from 'react-i18next';

import ClickableDenseTable from './ClickAbleDenseTable/ClickAbleDenseTable.js';
import BankBalanceComparison from './BankBalanceComparison.jsx';
import MetricChip from '../Widgets/MetricChip/MetricChip.jsx';
import Constants from '../../assests/Constants/constants';
import '../../styles/bankTokens.css';

const currencyOptions = Object
    .entries(Constants.ForeignCurrencyCode)
    .map(([code, num]) => ({ label: code, value: num }));

/** Currency badge for an account, e.g. "USD ($)". Null for local currency. */
const defaultCurrencyLabel = (account) => {
    if (!account) return null;
    const code = currencyOptions.find((o) => o.value === account.foreignCurrencyCode)?.label;
    if (!code) return null;
    const symbol = Constants.ForeignCurrencySymbol[code] || '';
    return symbol ? `${code} (${symbol})` : code;
};

const parseAmount = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const cleaned = String(value).replace(/[^\d.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const firstRow = (rows) => (Array.isArray(rows) && rows.length > 0 ? rows[0] : null);

const displayOf = (value) => (value == null || value === '' ? '—' : String(value).trim());

/** A delta of zero means the two sources agree, which is the good outcome. */
const isSettled = (value) => {
    const amount = parseAmount(value);
    return amount != null && Math.abs(amount) < 0.01;
};

const barSx = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 1,
    px: 1.25,
    py: 0.75,
    backgroundColor: 'var(--bank-surface, #fff)',
    border: '1px solid var(--bank-border, #e4e7f2)',
    borderRadius: 'var(--bank-radius, 12px)',
    boxShadow: 'var(--bank-shadow, 0 2px 8px rgba(0, 0, 0, 0.08))',
};

/** One group of related controls inside the bar. */
const sectionSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    flexWrap: 'wrap',
};

const SectionDivider = () => (
    <Divider
        orientation="vertical"
        flexItem
        sx={{ my: 0.25, borderColor: 'var(--bank-border, #e4e7f2)' }}
    />
);

const SOURCE_ACCENTS = {
    xtram: '#F9AA2A',
    finsite: '#0D9488',
    diff: '#64748B',
};

const matrixCellSx = {
    px: 0.9,
    py: 0.2,
    fontVariantNumeric: 'tabular-nums',
    direction: 'ltr',
    unicodeBidi: 'isolate',
    textAlign: 'center',
    whiteSpace: 'nowrap',
};

/**
 * Xtram against Finsite as a small labelled grid, one row per source plus the
 * difference between them. Reading a column top to bottom answers "do the books
 * agree with the bank", which a single line of run-together figures does not.
 */
const BalanceMatrix = ({ xtram, finsite, diff, showDiff, loading, t }) => {
    const rows = [
        { key: 'xtram', label: t('Xtram'), values: xtram },
        { key: 'finsite', label: t('Finsite'), values: finsite, loading },
        ...(showDiff ? [{ key: 'diff', label: t('Difference'), values: diff, isDiff: true }] : []),
    ];

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'auto repeat(3, minmax(86px, auto))',
                alignItems: 'center',
                rowGap: 0.1,
                border: '1px solid var(--bank-border, #e4e7f2)',
                borderRadius: 'var(--bank-radius-sm, 8px)',
                backgroundColor: 'var(--bank-surface, #fff)',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ px: 0.9, py: 0.25, backgroundColor: 'var(--bank-surface-muted, #f7f8fc)' }} />
            {[t('Balance'), t('Credit'), t('Debit')].map((header) => (
                <Typography
                    key={header}
                    sx={{
                        ...matrixCellSx,
                        py: 0.25,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        color: 'var(--bank-text-muted, #6b7186)',
                        backgroundColor: 'var(--bank-surface-muted, #f7f8fc)',
                        direction: 'inherit',
                    }}
                >
                    {header}
                </Typography>
            ))}

            {rows.map((row) => {
                const settled = row.isDiff && isSettled(row.values?.balance);
                const valueColor = row.isDiff
                    ? (settled ? 'var(--bank-positive, #1B8A5A)' : 'var(--bank-negative, #d32f2f)')
                    : 'var(--bank-text, #1f2437)';

                return (
                    <React.Fragment key={row.key}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.9,
                                py: 0.2,
                                borderInlineStart: `3px solid ${SOURCE_ACCENTS[row.key]}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    color: SOURCE_ACCENTS[row.key],
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {row.label}
                            </Typography>
                            {row.loading && (
                                <CircularProgress size={9} sx={{ color: SOURCE_ACCENTS[row.key] }} />
                            )}
                        </Box>
                        {['balance', 'credit', 'debit'].map((metric) => (
                            <Typography
                                key={metric}
                                sx={{
                                    ...matrixCellSx,
                                    fontSize: '0.72rem',
                                    fontWeight: row.isDiff ? 700 : 600,
                                    color: valueColor,
                                }}
                            >
                                {displayOf(row.values?.[metric])}
                            </Typography>
                        ))}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

/** "בנק 12 · סניף 841 · חשבון 4450217 · כרטיס 5" for the selected account. */
const useAccountDetail = (account, t) =>
    useMemo(() => {
        if (!account) return null;
        const parts = [
            [t('BanksColBankNo'), account.bankCode],
            [t('BanksColBranch'), account.branchNumber],
            [t('BanksColAccount'), account.accountNumber],
            [t('BanksColCard'), account.cardId],
        ]
            .filter(([, value]) => value != null && value !== '' && value !== '-')
            .map(([label, value]) => `${label} ${value}`);
        return parts.length > 0 ? parts.join(' · ') : null;
    }, [account, t]);

/**
 * Compact bank context strip: which account is being reconciled, over which
 * period, and whether its balance agrees with the bank. The full account table
 * and the Xtram/Finsite breakdown move into a popover so the workspace keeps
 * the vertical space for transactions.
 */
const BankAccountBar = ({
    selectedAccount = null,
    selectedAccountIndex = -1,
    onSelectAccount,
    denseTableHeader = [],
    denseTableRows = [],
    onEditAccount,
    onDeleteSelectedAccounts,
    getCurrencyLabel,
    xtramRows = [],
    finsiteRows = [],
    diffRows = [],
    showFinsite = false,
    finsiteLoading = false,
    selectedRows = null,
    selectedCount = 0,
    accountCardSlot = null,
    periodSlot = null,
    modeSlot = null,
    actionsSlot = null,
    direction = 'rtl',
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);

    const xtram = useMemo(() => firstRow(xtramRows) || {}, [xtramRows]);
    const finsite = useMemo(() => firstRow(finsiteRows) || {}, [finsiteRows]);
    const diff = useMemo(() => firstRow(diffRows) || {}, [diffRows]);

    const selected = useMemo(() => firstRow(selectedRows) || {}, [selectedRows]);
    const showSelected = selectedCount > 0 && Array.isArray(selectedRows) && selectedRows.length > 0;

    const currencyLabel = (getCurrencyLabel || defaultCurrencyLabel)(selectedAccount);
    const accountDetail = useAccountDetail(selectedAccount, t);

    const accountTitle = selectedAccount
        ? (selectedAccount.bankName && selectedAccount.bankName !== '-'
            ? selectedAccount.bankName
            : t('BankAccount'))
        : t('SelectBankAccount');

    // Only compare once the account is linked and Finsite has actually answered.
    const showComparison = showFinsite && (finsiteLoading || finsite.balance != null);
    const finsiteLabel = t('Finsite');

    const handleSelectFromTable = (index) => {
        onSelectAccount?.(index);
        setAnchorEl(null);
    };

    const finsiteBadge = selectedAccount ? (
        <Chip
            size="small"
            icon={showFinsite
                ? <CloudDoneIcon sx={{ fontSize: 14 }} />
                : <CloudOffIcon sx={{ fontSize: 14 }} />}
            label={`${finsiteLabel} · ${showFinsite ? t('FinsiteLinked') : t('FinsiteNotLinked')}`}
            sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                color: showFinsite ? '#0D9488' : 'rgba(15, 23, 42, 0.45)',
                backgroundColor: showFinsite ? 'rgba(13, 148, 136, 0.1)' : 'rgba(15, 23, 42, 0.04)',
                '& .MuiChip-icon': {
                    color: 'inherit',
                    marginInlineStart: '6px',
                    marginInlineEnd: '-4px',
                },
                '& .MuiChip-label': { px: 0.75 },
            }}
        />
    ) : null;

    const accountSection = (
        <Box sx={sectionSx}>
            <Tooltip title={accountDetail || t('BankAccountsAndBalances')} arrow>
                <Button
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    startIcon={<AccountBalanceIcon fontSize="small" />}
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    sx={{
                        textTransform: 'none',
                        height: 'calc(var(--xtram-control-height, 36px) + 4px)',
                        minHeight: 'calc(var(--xtram-control-height, 36px) + 4px)',
                        maxHeight: 'calc(var(--xtram-control-height, 36px) + 4px)',
                        boxSizing: 'border-box',
                        color: selectedAccount
                            ? 'var(--bank-text, #1f2437)'
                            : 'var(--bank-text-muted, #6b7186)',
                        border: '2px solid var(--filter-border, #e5e7f6)',
                        borderRadius: '8px',
                        px: 1,
                        py: 0,
                        maxWidth: 400,
                        justifyContent: 'flex-start',
                        textAlign: 'start',
                        '& .MuiButton-startIcon': { marginInlineEnd: '6px' },
                        '& .MuiButton-endIcon': { marginInlineStart: 'auto' },
                        '&:hover': {
                            borderColor: 'var(--Foundation-Blue-Normal, #304FFF)',
                            backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, lineHeight: 1.2 }}>
                        <Box
                            component="span"
                            sx={{
                                fontSize: 'var(--xtram-text-sm, 13px)',
                                fontWeight: 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            {accountTitle}
                        </Box>
                        {currencyLabel && (
                            <Chip
                                label={currencyLabel}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    color: 'var(--Foundation-Blue-Normal, #304FFF)',
                                    backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}
                        {finsiteBadge}
                    </Box>
                </Button>
            </Tooltip>

            {accountCardSlot && (
                <Box sx={{ minWidth: 200, maxWidth: 300, flex: '1 1 200px' }}>{accountCardSlot}</Box>
            )}
        </Box>
    );

    const balancesSection = (
        <Box sx={sectionSx}>
            {showComparison ? (
                <BalanceMatrix
                    xtram={xtram}
                    finsite={finsite}
                    diff={diff}
                    showDiff={!finsiteLoading && diffRows.length > 0}
                    loading={finsiteLoading}
                    t={t}
                />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <MetricChip label={t('Balance')} value={xtram.balance} />
                    <MetricChip label={t('Credit')} value={xtram.credit} />
                    <MetricChip label={t('Debit')} value={xtram.debit} />
                </Box>
            )}

            {/* What the current selection adds up to, shown only while rows are
                selected so the bar stays quiet the rest of the time. */}
            {showSelected && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 0.75,
                        py: 0.4,
                        borderRadius: 'var(--bank-radius-sm, 8px)',
                        backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)',
                        border: '1px solid rgba(48, 79, 255, 0.25)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            color: 'var(--Foundation-Blue-Normal, #304FFF)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`${t('SelectedRows')} (${selectedCount})`}
                    </Typography>
                    {[
                        [t('Balance'), selected.balance],
                        [t('Credit'), selected.credit],
                        [t('Debit'), selected.debit],
                    ].map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                    color: 'var(--bank-text-muted, #6b7186)',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    color: 'var(--bank-text, #1f2437)',
                                    whiteSpace: 'nowrap',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {displayOf(value)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );

    /* One bar, read start to end: which account, over which period, what is being
       matched, where the balance stands, and what can be done to it. */
    return (
        <Box sx={barSx} dir={direction}>
            {accountSection}

            {periodSlot && (
                <>
                    <SectionDivider />
                    <Box sx={sectionSx}>{periodSlot}</Box>
                </>
            )}

            {modeSlot && (
                <>
                    <SectionDivider />
                    <Box sx={sectionSx}>{modeSlot}</Box>
                </>
            )}

            <Box sx={{ flex: '1 1 auto', minWidth: 0 }} />

            {balancesSection}

            {actionsSlot && (
                <>
                    <SectionDivider />
                    <Box
                        className="document-action-chips"
                        sx={{ ...sectionSx, gap: 0.5 }}
                    >
                        {actionsSlot}
                    </Box>
                </>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.75,
                            p: 1.5,
                            width: 'min(940px, 96vw)',
                            borderRadius: 'var(--bank-radius, 12px)',
                            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.16)',
                        },
                    },
                }}
            >
                <Box dir={direction} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--bank-text, #1f2437)' }}>
                            {t('BankAccountsAndBalances')}
                        </Typography>
                        {selectedAccount && (
                            <>
                                <Typography
                                    sx={{ fontSize: '0.78rem', color: 'var(--bank-text-muted, #6b7186)' }}
                                >
                                    {[accountTitle, accountDetail, currencyLabel]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Typography>
                                {finsiteBadge}
                            </>
                        )}
                    </Box>

                    <ClickableDenseTable
                        headers={denseTableHeader}
                        content={denseTableRows}
                        styleFlag={false}
                        numericrows={false}
                        setSelectedRow={handleSelectFromTable}
                        selectedRowIndex={selectedAccountIndex}
                        align="left"
                        reverse
                        width="100%"
                        height="320px"
                        openEdit={onEditAccount}
                        enableFilter
                        enableMultiSelect
                        onDeleteSelected={onDeleteSelectedAccounts}
                    />

                    <BankBalanceComparison
                        xtramRows={xtramRows}
                        finsiteRows={finsiteRows}
                        diffRows={diffRows}
                        showFinsite={showFinsite}
                        finsiteLoading={finsiteLoading}
                        selectedRows={selectedRows}
                        selectedCount={selectedCount}
                    />
                </Box>
            </Popover>
        </Box>
    );
};

export default BankAccountBar;
